//#region [IMPORT]

// === THIRD-PARTY ===
import React, { useState, useEffect, useDeferredValue, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';

// ### MUI / STYLES ###
import {
  Box,
  Checkbox,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
} from '@mui/material';
import { Delete, Cancel, CheckCircle, UnfoldMoreOutlined } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';

// === UTILS ===
import { getHexAlpha } from 'components/styles.utils';

// === API ===
import { on, removeListener, sendMessage } from 'api/prokey/prokey.websocket';

// === COMPONENTS ===
import { ControlBox } from './components/ControlBox/index';

//#endregion

const DOCUMENT_COMPARE_METHODS = {
  is_active: (a, b) => {
    return (a || 0) - (b || 0);
  },
  description: (a, b) => {
    const cmp_a = (a || '').toUpperCase();
    const cmp_b = (b || '').toUpperCase();
    return cmp_a < cmp_b ? -1 : cmp_a > cmp_b ? 1 : 0;
  },
  comment: (a, b) => {
    const cmp_a = (a || '').toUpperCase();
    const cmp_b = (b || '').toUpperCase();
    return cmp_a < cmp_b ? -1 : cmp_a > cmp_b ? 1 : 0;
  },
  tags: (a, b) => {
    return (a || 0) - (b || 0);
  },
  createdAt: (a, b) => {
    return (new Date(a) || 0) - (new Date(b) || 0);
  },
  updatedAt: (a, b) => {
    return (new Date(a) || 0) - (new Date(b) || 0);
  },
};

//-----------------------------------------------------------------
export function RuleList() {
  //-----------------------------------------------------------------

  //#region [VALUES]

  const theme    = useTheme();
  const navigate = useNavigate();

  const [ rules       , setRules      ] = useState( [] );
  const [ sort_header , setSortHeader ] = useState( 'description' );
  const [ sort_ascend , setSortAscend ] = useState( true );
  const [ search      , setSearch     ] = useState( null );
  const [ filters     , setFilters    ] = useState( [] );

  const search_deffered = useDeferredValue( search );

  //#endregion

  //#region [STYLES]

  const _styles = {
    task_queue_text: {
      fontSize: 'x-large',
      paddingLeft: '1rem',
    },
    table_header: {
      // boxShadow : '0 0 0.5em 0.01em' + theme.palette.primary.main,
    },
    table_header_cell: {
      backgroundColor: theme.palette.primary.main,// + getHexAlpha(100),
      color: theme.palette.primary.contrastText,
      textAlign: 'center',
    },
    table_container: {
      height    : '100%',
      width     : '100%',
      maxWidth  : '100%',
      overflowY : 'hidden',
    },
    table_container2: {
      height    : '100%',
      width     : '100%',
      maxWidth  : '100%',
      padding   : '0',
      overflowY : 'auto',
    },
    card: {
      display      : 'grid',
      'gridTemplateColumns': 'repeat(  1, 1fr )',
      'gridTemplateRows'   : 'max-content 1fr',
      height       : 'calc( 100% - 2rem )' ,
      width        : 'calc( 100% - 2rem )' ,
      maxHeight    : '100%'   ,
      maxWidth     : '100%'   ,
      overflowY    : 'hidden' ,
      marginTop    : '1rem'   ,
      marginLeft   : '1rem'   ,
      marginRight  : '1rem'   ,
      border       : '1px solid ' + theme.palette.background.primary[theme.palette.mode],
      borderRadius : '1rem'   ,
      backgroundColor: theme.palette.background.primary[theme.palette.mode],
    },
  };

  //-----------------------------------------------------------------
  function createHeaderProps( header, property ) {
    //-----------------------------------------------------------------
    const style = {
      cursor: 'pointer',
      backgroundColor: sort_header === header ? theme.palette.primary.dark : theme.palette.primary.main + getHexAlpha(100),
      color: theme.palette.primary.contrastText,
    }

    return {
      sx            : style,
      sortDirection : sort_ascend ? 'asc' : 'desc',
      onClick       : () => onClickHeader( property ),
      children      : <Box sx={{display: 'flex'}}><UnfoldMoreOutlined />{ header }</Box>
    }
  }

  //#endregion

  //#region [EFFECT]

  useEffect(() => {
    //-----------------------------------------------------------------
    function listener(data) {
      //-----------------------------------------------------------------
      setRules( data.rules );
    }

    on( 'ANALYTICS_RULES' , listener );
    
    const message = JSON.stringify( { type: 'ANALYTICS_RULES_REQUEST' } );
    sendMessage( message );

    return () => {
      removeListener( 'ANALYTICS_RULES' , listener );
    };
  }, []); // empty dependency = called only once at startup

  //#endregion

  //#region [EVENTS]

  //-----------------------------------------------------------------
  function handleFilter( value ) {
    //-----------------------------------------------------------------
    try {
      setFilters( value );
    } catch (error) {
      console.warn(error);
    }
  }

  //-----------------------------------------------------------------
  function handleSearch( value ) {
    //-----------------------------------------------------------------
    try {
      setSearch( value );
    } catch (error) {
      console.warn(error);
    }
  }

  //-----------------------------------------------------------------
  function onClickHeader( name ) {
    //-----------------------------------------------------------------
    if( sort_header === name ) {
      setSortAscend( !sort_ascend );
    } else {
      setSortAscend( true );
      setSortHeader( name );
    }
  }

  //#endregion

  //#region [RENDER]

  //-----------------------------------------------------------------
  function display() {
    //-----------------------------------------------------------------
    return (
      <Box sx={_styles.card}>
        <ControlBox count={rules.length} onFilter={handleFilter} onSearch={handleSearch}/>
        <TableContainer sx={_styles.table_container2}>
          <Table stickyHeader size='small'>
            <TableHead sx={_styles.table_header}>
              <TableRow>
                <TableCell sx={_styles.table_header_cell} key={0}>{ '' }</TableCell>

                <TableCell key={1} { ...createHeaderProps( 'Enabled'     , 'is_active'   ) } />
                <TableCell key={2} { ...createHeaderProps( 'Description' , 'description' ) } />
                <TableCell key={3} { ...createHeaderProps( 'Comment'     , 'comment'     ) } />
                <TableCell key={5} { ...createHeaderProps( 'Created'     , 'createdAt'   ) } />
                <TableCell key={6} { ...createHeaderProps( 'Updated'     , 'updatedAt'   ) } />

                <TableCell sx={_styles.table_header_cell} key={7}>{ '' }</TableCell>
              </TableRow>
            </TableHead>
            <List values={rules} sort_header={sort_header} sort_ascend={sort_ascend} filters={filters} search={search_deffered} />
          </Table>
        </TableContainer>
      </Box>
    );
  }

  //#endregion

  return display();
}


//-----------------------------------------------------------------
const List = memo( function List( props ) {
  //-----------------------------------------------------------------

  //#region [VALUES]

  const { values, sort_header, sort_ascend, filters, search } = props;

  const theme    = useTheme();
  const navigate = useNavigate();

  const [ values_filtered , setValuesFiltered ] = useState( [] )
  const [ selected        , setSelected       ] = useState( {} );

  //#endregion

  //#region [STYLES]

  const _styles = {
    table_row: {
      cursor: 'pointer',
      '&:hover': {
        backgroundColor: theme.palette.primary.main + getHexAlpha(20),
      },
      '&.Mui-selected': {
        backgroundColor: theme.palette.primary.main + getHexAlpha(20),
        '&:hover': {
          backgroundColor: theme.palette.primary.main + getHexAlpha(30),
        },
      },
    },
    table_cell: {
      // textAlign: 'center',
      justifyContent: 'center',
      borderBottom: "none",
    },
  };

  //#endregion

  //#region [EFFECT]

  useEffect(() => {
    try {
      const has_filters  = filters.length > 0;
      let filtered_rules = has_filters ? values.filter( rule => filters.every( filter => filter(rule) ) ) : values;
      if( search ) {
        filtered_rules = filtered_rules.filter( rule => rule.description?.toUpperCase().includes( search ) );
      }
      filtered_rules.sort((a, b) => DOCUMENT_COMPARE_METHODS[sort_header](a[sort_header], b[sort_header]));
      if( !sort_ascend ) {
        filtered_rules.reverse();
      }
  
      setValuesFiltered( [...filtered_rules] )
    } catch (error) {
      console.warn(error);
    }
  }, [values, sort_header, sort_ascend, filters, search]);

  //#endregion

  //#region [EVENTS]

  //-----------------------------------------------------------------
  function onClick(id) {
    //-----------------------------------------------------------------
    try {
      const new_selected = {...selected};
      const has_id = new_selected.hasOwnProperty(id);
      
      if(has_id) delete new_selected[id];
      else new_selected[id] = true;

      setSelected(new_selected);
    } catch (error) {
      console.warn(error);
    }
  }

  //-----------------------------------------------------------------
  function onDoubleClick(event) {
    //-----------------------------------------------------------------
    try {
      navigate( '/analytics/rules/' + event.currentTarget.id, { replace: true } );
    } catch (error) {
      console.warn(error);
    }
  }

  //-----------------------------------------------------------------
  function onClick_Remove(id) {
    //-----------------------------------------------------------------
    try {
      const message = JSON.stringify( { type: 'RULE_REMOVE', data: { id } } );
      sendMessage( message );
    } catch (error) {
      console.warn(error);
    }
  }

  //-----------------------------------------------------------------
  function onClick_IsActive(id) {
    //-----------------------------------------------------------------
    try {
    
      const message = JSON.stringify( { type: 'ANALYTICS_RULE_ACTIVE_TOGGLE', data: { id } } );
      sendMessage( message );

    } catch (error) {
      console.warn(error);
    }
  }

  //#endregion

  //#region [RENDER]

  //-----------------------------------------------------------------
  function display() {
    //-----------------------------------------------------------------
    const has_rules = values.length > 0;
    return (
      <TableBody size='small'>{ has_rules ? displayValues() : displayEmpty() }</TableBody>
    );
  }

  //-----------------------------------------------------------------
  function displayValues() {
    //-----------------------------------------------------------------
    const result = [];

    for( const rule of values_filtered ) {
      const is_selected = selected.hasOwnProperty(rule.id);
      const date_created = dayjs( rule.createdAt ).format('DD/MM/YYYY - HH:MMa');
      const date_updated = dayjs( rule.updatedAt ).format('DD/MM/YYYY - HH:MMa')

      result.push(
        <TableRow sx={_styles.table_row} key={rule.id} id={rule.id} 
          onClick={()=>{onClick(rule.id)}}
          onDoubleClick={onDoubleClick}
          selected={is_selected}
        >
          <TableCell sx={_styles.table_cell} key={0}>
            <Delete onClick={()=>{onClick_Remove(rule.id)}} />
          </TableCell>
          <TableCell sx={_styles.table_cell} key={1}>
            <Checkbox disabled checked={rule.is_active} onClick={onClick_IsActive} />
          </TableCell>
          <TableCell sx={_styles.table_cell} key={2}> { rule.description } </TableCell>
          <TableCell sx={_styles.table_cell} key={3}> { rule.comment     } </TableCell>
          {/* <TableCell sx={_styles.table_cell} key={4}> { JSON.stringify(rule.tags) } </TableCell> */}
          <TableCell sx={_styles.table_cell} key={5}> { date_created } </TableCell>
          <TableCell sx={_styles.table_cell} key={6}> { date_updated } </TableCell>
          <TableCell sx={_styles.table_cell} key={7}>  </TableCell>
        </TableRow>
      );
    }

    return result;
  }

  //-----------------------------------------------------------------
  function displayEmpty() {
    //-----------------------------------------------------------------
    return (
      <TableRow>
        <TableCell colSpan='7' className='allEmptyHere'>
          All empty here...
        </TableCell>
      </TableRow>
    );
  }

  //#endregion

  return display();
})